/**
 * The Diamond Sutra — editorial authority essays, tracks 8–12.
 * Thematic analysis only. No recording facts, credits, durations, lyrics,
 * or streaming destinations are asserted here.
 */
import type { SongEssay } from "./types";

export const DIAMOND_SUTRA_ESSAYS_8_12: Record<string, SongEssay> = {
  "before-the-bloom": {
    seoTitle: "Before the Bloom — Buddha KA | The Diamond Sutra",
    seoDescription:
      "The unseen formation that precedes any visible result. Buddha KA on roots, patience under no applause, and why the period before evidence is the period that decides everything.",
    standfirst:
      "Everything visible was assembled somewhere no one was watching.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Before the Bloom” concerns the unwitnessed interval — the stretch of development that produces a result long before anyone can see one. It is the part of any life that receives no confirmation, and therefore the part most people abandon.",
          "The botanical image is chosen for its accuracy rather than its prettiness. A root system does not resemble a flower. Nothing about the underground phase advertises what it is building, and judged on appearance it looks like nothing is happening at all.",
          "The song's interest is in what a person does during that interval. Not how they endure it emotionally, but what they actually construct while there is no audience and no evidence.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Formation Without Feedback",
        paragraphs: [
          "Most systems people rely on for motivation are feedback systems: response, recognition, measurable progress. The formation period supplies none of them, which is why it filters so effectively.",
          "What replaces feedback is design. In the absence of external signal, the only thing keeping work coherent is an internal standard the person set deliberately and refuses to renegotiate under boredom.",
          "This is where the album's language of internal design becomes concrete. A structure built to run without applause is a different structure from one built to attract it, and the difference is only visible later.",
        ],
      },
      {
        eyebrow: "The idea",
        title: "Depth Determines Ceiling",
        paragraphs: [
          "Visible outcomes are constrained by invisible ones. Capacity, discipline, and clarity are all built below the line, and nothing can appear above it that was not supported below.",
          "This reframes patience as engineering rather than virtue. The person waiting is not being noble; they are still building, and the wait is the build.",
          "It also reframes premature visibility as a risk. Attention arriving before structure does not accelerate growth — it loads a system that has not been rooted for it.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Why It Matters in the Album",
        paragraphs: [
          "By this point The Diamond Sutra has taken apart inherited seeing, inherited thinking, and inherited wanting. “Before the Bloom” begins the turn toward construction: what is built once the borrowed material has been named.",
          "The record's later chapters on mechanism, possibility, and value all assume this interval. None of them describe instant transformation; all of them describe systems that take time to hold.",
          "Placed here, the track functions as a pause with intent — the album's acknowledgment that dismantling is fast and building is not.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for restraint. A song about pre-visible work would betray itself by rushing to the payoff.",
          "Notice the pull toward proof. The discomfort of an unresolved stretch is precisely the condition being examined.",
          "Afterward, name one thing currently in formation that no one else can see. That naming is the song's practical use.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "field-of-dreams",
        title: "Field of Dreams",
        reason: "The imagination that gives formation a direction worth holding.",
      },
      {
        songSlug: "diamond",
        title: "Diamond",
        reason: "What pressure and duration produce when the structure survives them.",
      },
    ],
  },

  "spiritual-mechanics": {
    seoTitle: "Spiritual Mechanics — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Spiritual life treated as a working system. Buddha KA examines attention, habit, intention, and consequence as mechanisms with observable behaviour — rigorous inquiry rather than mystical language.",
    standfirst:
      "Not atmosphere. Mechanism. The interior runs on rules that can be studied.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Spiritual Mechanics” takes a deliberately unromantic position: the inner life behaves like a system, and systems can be understood. Inputs produce outputs. Repetition produces structure. Attention allocates energy whether or not anyone is supervising it.",
          "The title is a correction as much as a description. Much spiritual language is atmospheric — it describes how something feels without ever specifying what is happening. That vagueness is comfortable and useless.",
          "This track argues for precision instead. If a practice works, there is a reason it works, and knowing the reason is what allows a person to apply it when conditions change.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "The Working Parts",
        paragraphs: [
          "Attention is the primary currency: what receives it grows, what is denied it weakens, and this occurs regardless of intention or belief. That is the first mechanic, and most of the others are downstream of it.",
          "Habit is compression. It takes decisions that once required deliberation and converts them into architecture, which is why habits feel like identity from the inside. Cause and effect operate across longer intervals than the mind naturally tracks, which is why consequence so often looks like fate.",
          "Intention functions as a routing instruction rather than a wish. It determines where the system sends its resources before conscious deliberation ever begins.",
        ],
      },
      {
        eyebrow: "The standard",
        title: "Rigour Instead of Mystery",
        paragraphs: [
          "Treating the interior as a mechanism does not strip it of depth. Precision has never diminished anything worth studying; it makes the depth navigable.",
          "The alternative — leaving inner life in the language of vibes — surrenders authorship. Whatever cannot be described cannot be adjusted, and a person left with only atmosphere has to wait for weather.",
          "The song holds a line here that runs through the whole album: reverence and analysis are not opposed. Buddhist philosophical inquiry has always been closer to investigation than to sentiment.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "This track anchors the album's inner-systems sequence, which continues through its examinations of divinity, embodiment, and value.",
          "It also supplies the operating manual for what came earlier. Diagnosing inherited perception and inherited desire is only useful if something can be done with the diagnosis, and mechanics is where the doing lives.",
          "The record's title metaphor applies cleanly. A blade that cuts precisely requires knowledge of the material.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for the refusal of vagueness. The song's authority comes from specificity, not from atmosphere.",
          "Notice which mechanism describes something you have already observed but never named. Recognition is the useful reaction here, not agreement.",
          "Then test one of them for a week. Mechanics is a claim about behaviour, and behaviour can be checked.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "create",
        title: "Create",
        reason: "Mechanism turned outward — the same lawfulness applied to making something.",
      },
      {
        songSlug: "different-math",
        title: "Different Math",
        reason: "The accounting layer — how consequence is measured once mechanism is understood.",
      },
    ],
  },

  "tunnel-view": {
    seoTitle: "Tunnel View — Buddha KA | The Diamond Sutra",
    seoDescription:
      "When focus becomes blindness. Buddha KA examines narrowed perception, fixation, and everything that disappears when consciousness is locked inside a single interpretation.",
    standfirst:
      "A narrow field feels like clarity from the inside. That is the trap.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Tunnel View” examines the failure mode of concentration. Attention that narrows enough stops being a tool and becomes a wall, and the person inside it experiences the restriction as certainty.",
          "The condition is not stupidity. It is usually the product of pressure, urgency, or fear, all of which are efficient at collapsing a field of awareness into a single corridor. Under threat, narrowing is adaptive; sustained past the threat, it becomes a way of living.",
          "The song's concern is with what falls outside the corridor: alternatives, context, other people's interiors, and the possibility that the emergency being responded to ended some time ago.",
        ],
      },
      {
        eyebrow: "The distinction",
        title: "Focus and Blindness",
        paragraphs: [
          "Both look identical from the first-person view. Both feel like commitment, and both produce the sensation of knowing exactly what matters.",
          "The difference is reversibility. Focus can widen on request; a tunnel cannot. When a person is unable to describe a credible alternative reading of their situation, they are not concentrating — they are locked.",
          "There is also a temporal marker. Focus is chosen and periodically reviewed. Fixation has no review date, and no one inside it remembers scheduling one.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "What the Corridor Costs",
        paragraphs: [
          "A narrowed field does not merely exclude information; it reorganises everything remaining around one interpretation. Neutral events become evidence. Unrelated people become participants.",
          "This is how a single frame becomes a whole world. The tunnel is self-confirming because it filters the input that could contradict it, and each confirmation deepens the walls.",
          "The exit is unglamorous: deliberately introduce the excluded material. Ask what would have to be true for the opposite reading to hold, and pursue that question honestly rather than defensively.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "The album's conditioning sequence examined inherited lenses. “Tunnel View” examines a lens a person builds themselves, in real time, usually under strain.",
          "That makes it the harder case. Inherited framing can be traced to a source and returned; self-manufactured framing has no external author to blame, only a decision to maintain.",
          "It also answers the album's opening. Gathering attention is the first move; noticing when gathered attention has clamped shut is the correction.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for compression — the sensation of the field getting smaller as the track proceeds.",
          "Notice the situation your own mind returns to. Whatever it re-enters unprompted is the current corridor.",
          "Then widen deliberately. The relief of a restored field is the song's real argument.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "break-the-frame",
        title: "Break the Frame",
        reason: "The structural counterpart — who decided where the picture ends.",
      },
      {
        songSlug: "collect-my-thoughts",
        title: "Collect My Thoughts",
        reason: "The opposite failure: attention scattered rather than clamped.",
      },
    ],
  },

  diamond: {
    seoTitle: "Diamond — Buddha KA | The Diamond Sutra",
    seoDescription:
      "The album's title metaphor at full weight. Buddha KA on clarity formed under pressure, an edge that cuts illusion, and a mind that cannot be scratched by what it has already seen through.",
    standfirst:
      "Hardness that came from pressure, clarity that came from time, an edge that only removes what was never real.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Diamond” carries the album's central image, and the song treats it structurally rather than decoratively. The interest is in the properties, not the price.",
          "A diamond is defined by three behaviours: it forms under sustained pressure, it transmits light rather than absorbing it, and it cuts materials that cannot cut it. Each maps onto a claim the record has been building.",
          "What the track deliberately avoids is the status reading. In a culture where the stone signifies wealth and display, reclaiming it as a description of consciousness is itself an act of reframing.",
        ],
      },
      {
        eyebrow: "The idea",
        title: "Pressure as Formation",
        paragraphs: [
          "Pressure is not presented here as suffering to be admired. It is presented as a condition under which loose material becomes dense material, and density is what makes an edge possible.",
          "The distinction matters because glorified hardship is a familiar trap. Not all pressure produces structure; pressure without direction produces damage. What converts one into the other is the internal design applied while it is being carried.",
          "This is the album's earlier formation chapter arriving at its result. What was rooted invisibly is now load-bearing.",
        ],
      },
      {
        eyebrow: "The edge",
        title: "Cutting Illusion",
        paragraphs: [
          "In the classical tradition the diamond blade is an image of discernment: it severs illusion without harming what is real, because it can only cut what is softer than truth.",
          "Applied to a mind, that means clarity is not aggression. A person who sees clearly does not need to attack a false idea; the idea simply stops holding weight in their presence.",
          "It also implies protection. Once something has genuinely been seen through, it cannot easily be reinstalled. A mind that has examined its own conditioning is not scratched by the next version of the same offer.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Why It Matters in the Album",
        paragraphs: [
          "Placed after the record's diagnostic and mechanical chapters, “Diamond” is the point where the album names what all that examination was for.",
          "It is the hinge into the final movement, where the record turns toward speech, divinity, embodiment, and a different measure of value.",
          "It also states the title's meaning without explaining it away: The Diamond Sutra is a record about a mind that has become difficult to deceive.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for hardness without noise. Density is not the same as volume, and the distinction is the song's aesthetic argument.",
          "Notice what the track refuses to soften. Clarity that apologises for itself is not clarity.",
          "Then ask what in your own thinking would survive being cut. Whatever remains is the working material.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "before-the-bloom",
        title: "Before the Bloom",
        reason: "The formation period that made the density possible.",
      },
      {
        songSlug: "borrowed-minds",
        title: "Borrowed Minds",
        reason: "The illusion the edge is for — inherited structure passing as original thought.",
      },
    ],
  },

  "speak-life": {
    seoTitle: "Speak Life — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Speech as causal participation. Buddha KA examines how language shapes attention, relationship, and identity — and why that is a discipline of accuracy rather than forced positivity.",
    standfirst:
      "Words do not describe a situation from a safe distance. They enter it.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Speak Life” treats speech as an action with consequences rather than a report on conditions. What is said aloud directs attention, and directed attention changes what grows.",
          "The song is careful to avoid the shallow version of this idea. Forced positivity is not what is being described. Denial spoken confidently is still denial, and it damages the speaker's relationship with reality.",
          "What is being proposed is stricter: speak accurately, and choose which accurate thing to make louder. There are always several true statements available about any situation, and the choice among them is where the power sits.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "How Speech Participates",
        paragraphs: [
          "Language allocates attention, and attention allocates energy. A repeated description becomes the primary lens through which a person meets their own circumstances.",
          "It also operates socially. What is said about another person shapes how they are treated, how they are perceived, and often how they perceive themselves. Speech installs frames in other people at a speed that thought never manages alone.",
          "And it operates on identity. Self-description is a instruction set. The sentences a person uses about themselves most often are the ones their behaviour eventually organises around.",
        ],
      },
      {
        eyebrow: "The discipline",
        title: "Accuracy Before Affirmation",
        paragraphs: [
          "The useful standard is not optimism; it is precision plus intention. Say the true thing, and say the true thing that opens a door rather than seals one.",
          "This requires more work than positivity. It means knowing the situation well enough to identify which truth is structural and which is incidental, then giving voice to the one that carries the future.",
          "Restraint belongs here too. A great deal of harm is done by accurate statements spoken carelessly, and knowing what not to amplify is part of the craft.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "The album's earlier writing chapter examined language as an instrument of consciousness. This track is its spoken counterpart, where the instrument is used in public and in real time.",
          "It also completes an arc. The record spends its first half showing how inherited language installs belief; here the listener is handed the same power and asked what they intend to do with it.",
          "That handover is deliberate. An album about conditioning that never addressed the listener's own transmitting capacity would be incomplete.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for weight distribution — which statements are given room and which are passed over.",
          "Notice your own default vocabulary afterward, particularly the sentences used most automatically about yourself.",
          "Then change one. The song's claim is testable in a single conversation.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "the-pen-breathes",
        title: "The Pen Breathes",
        reason: "The written instrument — the same discipline before it reaches the air.",
      },
      {
        songSlug: "unleash-divine",
        title: "Unleash Divine",
        reason: "What speech becomes when it is treated as embodiment rather than commentary.",
      },
    ],
  },
};
